import { useCallback } from "react";
import { useStopIrrigationEvent, useUpdateIrrigationEvent } from "@/api/orval/generated/irrigation-event";
import { useIrrigationEventCacheSync } from "./use-irrigation-event-cache-sync";

interface OnDemandEventParams {
    recordId: string;
}

interface EditOnDemandEventParams extends OnDemandEventParams {
    durationMs: number;
    /** The version to send with this update; callers choose it according to their editing semantics. */
    versionId?: number;
}

/**
 * The on-demand card's writes against a single event. Both answer false rather than throwing,
 * because the card's forms turn the outcome into a snackbar rather than an error boundary.
 */
export const useOnDemandEventWrites = () => {
    const { mutateAsync: updateEvent, isPending: updatePending } = useUpdateIrrigationEvent();
    const { mutateAsync: stopEvent, isPending: stopPending } = useStopIrrigationEvent();
    const { getDocId, commitUpdatedEvent, commitRemovedEvent } = useIrrigationEventCacheSync();

    const runWrite = useCallback(async (write: () => Promise<void>): Promise<boolean> => {
        try {
            await write();
            return true;
        } catch {
            // The mutation keeps the error; the caller only needs to know it failed.
            return false;
        }
    }, []);

    const editOnDemandEvent = useCallback(
        ({ recordId, versionId, durationMs }: EditOnDemandEventParams) =>
            runWrite(async () => {
                if (versionId === undefined) {
                    // runWrite turns this into the false the card's snackbar already handles.
                    throw new Error(`Cannot update irrigation event ${recordId} without a version`);
                }
                const docId = getDocId(recordId);
                const updated = await updateEvent({
                    irrigationEventId: recordId,
                    data: { defaultDuration: durationMs, versionId },
                });
                commitUpdatedEvent(updated, docId);
            }),
        [runWrite, updateEvent, getDocId, commitUpdatedEvent],
    );

    const stopOnDemandEvent = useCallback(
        ({ recordId }: OnDemandEventParams) =>
            runWrite(async () => {
                const docId = getDocId(recordId);
                const stopped = await stopEvent({ irrigationEventId: recordId });

                // The endpoint shortens a started irrigation and deletes one that has not started.
                if (stopped.deleted) {
                    commitRemovedEvent(recordId, docId);
                } else {
                    commitUpdatedEvent(stopped, docId);
                }
            }),
        [runWrite, stopEvent, getDocId, commitUpdatedEvent, commitRemovedEvent],
    );

    return { editOnDemandEvent, stopOnDemandEvent, loading: updatePending || stopPending };
};
